import React, { useState, useMemo, useEffect } from 'react';
import PageShell from '@/components/Layout/PageShell';
import { useRegisterUIContext } from '@/hooks';
import { useSearchParams } from 'react-router-dom';
import { getErrorDetail } from '@/utils/apiError';
import {
  useListCatalogueEntriesQuery,
  useUpdateCatalogueConfigMutation,
  useDeleteCatalogueConfigMutation,
  useListPlatformTenantsQuery,
  useListPlatformRolesQuery,
  useListPlatformUsersQuery,
  CatalogueEntry,
  LayerOverride,
} from '@/redux/internal-services/platformAdminApi';

// ── helpers ──────────────────────────────────────────────────────────────────

type Layer = 'system' | 'tenant' | 'role' | 'user';

function parseValue(type: string, raw: string): unknown {
  if (type === 'bool') return raw === 'true';
  if (type === 'int') return parseInt(raw, 10);
  if (type === 'list') {
    try { return JSON.parse(raw); } catch { return raw.split(',').map((s) => s.trim()); }
  }
  return raw;
}

function displayVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (Array.isArray(v)) return v.map((item) => (typeof item === 'object' && item !== null ? JSON.stringify(item) : String(item))).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function collectOverrides(overrides: LayerOverride[], settingKey: string) {
  return overrides
    .filter((o) => o.value[settingKey] !== undefined)
    .map((o) => ({ id: o.role_id ?? o.user_id ?? 'unknown', value: o.value[settingKey] }));
}

// ── override pill ─────────────────────────────────────────────────────────────

const PILL: Record<string, string> = {
  orange: 'bg-orange-50 border-orange-100 text-orange-700',
  purple: 'bg-purple-50 border-purple-100 text-purple-700',
  pink:   'bg-pink-50   border-pink-100   text-pink-700',
};
const DOT: Record<string, string> = {
  orange: 'bg-orange-400',
  purple: 'bg-purple-400',
  pink:   'bg-pink-400',
};

const OverridePill: React.FC<{ value: unknown; label?: string; color: string }> = ({ value, label, color }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2 py-0.5 border rounded text-xs font-mono max-w-[140px] truncate ${PILL[color]}`}
    title={label ? `${label}: ${displayVal(value)}` : displayVal(value)}
  >
    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${DOT[color]}`} />
    <span className="truncate">{displayVal(value)}</span>
  </span>
);

// ── inline editor cell ────────────────────────────────────────────────────────

const EditCell: React.FC<{
  value: unknown;
  metaType: string;
  allowedValues?: string[];
  isDefault: boolean;
  saving: boolean;
  onSave: (v: unknown) => void;
  onReset: () => void;
}> = ({ value, metaType, allowedValues, isDefault, saving, onSave, onReset }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(displayVal(value));

  const save = () => { onSave(parseValue(metaType, draft)); setEditing(false); };
  const cancel = () => setEditing(false);

  const isEnum = metaType === 'enum' && allowedValues && allowedValues.length > 0;

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        {metaType === 'bool' ? (
          <select
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="border border-ui-border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-dark/40 w-20"
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : isEnum ? (
          <select
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="border border-ui-border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-dark/40 w-36"
          >
            {allowedValues!.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <input
            autoFocus
            type={metaType === 'int' ? 'number' : 'text'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="border border-ui-border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-dark/40 w-32"
          />
        )}
        <button onClick={save} disabled={saving} className="px-2 py-1 bg-brand-dark text-white text-xs rounded hover:opacity-90 disabled:opacity-50 transition">
          {saving ? '…' : 'Save'}
        </button>
        <button onClick={cancel} className="px-2 py-1 text-ui-text3 text-xs hover:text-ui-text2 transition">✕</button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className={`text-xs font-mono ${isDefault ? 'text-ui-text3' : 'text-ui-text font-medium'}`}>
        {displayVal(value)}
      </span>
      <div className="flex items-center gap-1">
        <button onClick={() => { setDraft(displayVal(value)); setEditing(true); }} className="p-1 rounded hover:bg-brand-light/60 text-brand-dark" title="Edit">
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        {!isDefault && (
          <button onClick={onReset} className="p-1 rounded hover:bg-orange-50 text-orange-400" title="Reset to default">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

// ── catalogue card ────────────────────────────────────────────────────────────

const CatalogueCard: React.FC<{
  entry: CatalogueEntry;
  activeLayer: Layer;
  selectedTenantId: string;
  selectedRoleId: string;
  selectedUserId: string;
  onSave: (catalogueKey: string, settingKey: string, value: unknown, targetLayer: Layer) => void;
  savingKey: string | null;
  onDelete: (catalogueKey: string) => void;
  deletingKey: string | null;
  autoExpand?: boolean;
}> = ({ entry, activeLayer, selectedTenantId, selectedRoleId, selectedUserId, onSave, savingKey, onDelete, deletingKey, autoExpand }) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (autoExpand) setExpanded(true);
  }, [autoExpand]);

  // Catalogue-level editability for this layer
  const layerEditable = entry.layers?.[activeLayer]?.editable !== false; // default true if not declared

  // Filter settings to those visible for the active layer
  const allSettings = Object.entries(entry.settings ?? {});
  const settings = activeLayer === 'system'
    ? allSettings
    : allSettings.filter(([, sMeta]) => sMeta.layers?.[activeLayer]?.visible !== false);

  const tenantOverrideCount = Object.keys(entry.tenant_value ?? {}).length;
  const roleOverrideCount = entry.role_overrides?.length ?? 0;
  const userOverrideCount = entry.user_overrides?.length ?? 0;
  const anyOverride = tenantOverrideCount > 0 || roleOverrideCount > 0 || userOverrideCount > 0;

  // Which override column can be edited inline?
  const canEditTenant = layerEditable && activeLayer === 'tenant' && !!selectedTenantId;
  const canEditRole   = layerEditable && activeLayer === 'role'   && !!selectedRoleId;
  const canEditUser   = layerEditable && activeLayer === 'user'   && !!selectedUserId;

  // Whether the current focused scope has an override document to delete
  const hasOverrideToDelete =
    activeLayer !== 'system' && (
      (activeLayer === 'tenant' && selectedTenantId && Object.keys(entry.tenant_value ?? {}).length > 0) ||
      (activeLayer === 'role' && selectedRoleId && (entry.role_overrides ?? []).some((o) => o.role_id === selectedRoleId)) ||
      (activeLayer === 'user' && selectedUserId && (entry.user_overrides ?? []).some((o) => o.user_id === selectedUserId))
    );
  const isDeleting = deletingKey === entry.key;

  return (
    <div className="bg-ui-card border border-ui-border2 rounded-xl shadow-sm overflow-hidden">
      {/* header */}
      <button
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-ui-hover transition text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${anyOverride ? 'bg-orange-400' : 'bg-green-400'}`} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ui-text font-mono truncate">{entry.key}</p>
            {entry.description && (
              <p className="text-xs text-ui-text3 mt-0.5 truncate max-w-lg">{entry.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          <div className="hidden sm:flex items-center gap-2">
            {tenantOverrideCount > 0 && <span className="px-2 py-0.5 bg-orange-50 text-orange-500 text-xs rounded-full border border-orange-100">{tenantOverrideCount} tenant</span>}
            {roleOverrideCount > 0 && <span className="px-2 py-0.5 bg-purple-50 text-purple-500 text-xs rounded-full border border-purple-100">{roleOverrideCount} role</span>}
            {userOverrideCount > 0 && <span className="px-2 py-0.5 bg-pink-50 text-pink-500 text-xs rounded-full border border-pink-100">{userOverrideCount} user</span>}
            <span className="text-xs text-ui-text3">{settings.length} setting{settings.length !== 1 ? 's' : ''}</span>
          </div>
          {hasOverrideToDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(entry.key); }}
              disabled={isDeleting}
              className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600 disabled:opacity-50 transition"
              title="Delete this layer's override (reverts to previous layer)"
            >
              {isDeleting ? (
                <svg className="h-4 w-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              )}
            </button>
          )}
          <svg className={`h-4 w-4 text-ui-text3 transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* table */}
      {expanded && settings.length > 0 && (
        <div className="border-t border-ui-border2 overflow-x-auto">
          <table className="w-full min-w-[500px]">
            <thead>
              <tr className="bg-ui-raised text-xs text-ui-text3 uppercase tracking-wide">
                <th className="px-5 py-2.5 text-left font-medium w-[28%]">Setting</th>
                <th className="px-3 py-2.5 text-left font-medium w-10">Type</th>
                {activeLayer === 'system' && (
                  <>
                    <th className="px-3 py-2.5 text-left font-medium w-[12%]">Default</th>
                    <th className="px-3 py-2.5 text-left font-medium w-[24%]">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />System value</span>
                    </th>
                  </>
                )}
                {activeLayer === 'tenant' && (
                  <>
                    <th className="px-3 py-2.5 text-left font-medium w-[22%]">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />Tenant override</span>
                    </th>
                    <th className="px-3 py-2.5 text-left font-medium w-[22%]">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" />Effective value</span>
                    </th>
                  </>
                )}
                {activeLayer === 'role' && (
                  <>
                    <th className="px-3 py-2.5 text-left font-medium w-[22%]">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400 inline-block" />Role override</span>
                    </th>
                    <th className="px-3 py-2.5 text-left font-medium w-[22%]">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" />Effective value</span>
                    </th>
                  </>
                )}
                {activeLayer === 'user' && (
                  <>
                    <th className="px-3 py-2.5 text-left font-medium w-[22%]">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-pink-400 inline-block" />
                        User override
                        {!layerEditable && <span className="ml-1 text-ui-text3 italic normal-case">(read-only)</span>}
                      </span>
                    </th>
                    <th className="px-3 py-2.5 text-left font-medium w-[22%]">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" />Effective value</span>
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-ui-border2">
              {settings.map(([sKey, sMeta]) => {
                const systemVal   = (entry.system_value ?? {})[sKey];
                const tenantVal   = (entry.tenant_value ?? {})[sKey];
                const effectiveVal = (entry.effective_value ?? {})[sKey];
                const defaultVal  = sMeta.default;
                const systemIsDefault = systemVal === undefined || systemVal === defaultVal;
                const roleVals  = collectOverrides(entry.role_overrides ?? [], sKey);
                const userVals  = collectOverrides(entry.user_overrides ?? [], sKey);

                // The single override value for the focused role/user (when one is selected)
                const focusedRoleVal = selectedRoleId
                  ? (entry.role_overrides ?? []).find((o) => o.role_id === selectedRoleId)?.value?.[sKey]
                  : undefined;
                const focusedUserVal = selectedUserId
                  ? (entry.user_overrides ?? []).find((o) => o.user_id === selectedUserId)?.value?.[sKey]
                  : undefined;

                return (
                  <tr key={sKey} className="hover:bg-ui-hover transition-colors">
                    <td className="px-5 py-3 align-top">
                      <p className="text-xs font-medium font-mono text-ui-text">{sKey}</p>
                      {sMeta.description && <p className="text-xs text-ui-text3 mt-0.5 leading-snug">{sMeta.description}</p>}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <span className="px-1.5 py-0.5 bg-ui-subtle text-ui-text2 text-xs rounded font-mono">{sMeta.type}</span>
                    </td>

                    {/* System layer: default + system value (editable) */}
                    {activeLayer === 'system' && (
                      <>
                        <td className="px-3 py-3 align-top">
                          <span className="text-xs font-mono text-ui-text3">{displayVal(defaultVal)}</span>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <EditCell
                            value={systemVal !== undefined ? systemVal : defaultVal}
                            metaType={sMeta.type}
                            isDefault={systemIsDefault}
                            saving={savingKey === `system.${entry.key}.${sKey}`}
                            onSave={(v) => onSave(entry.key, sKey, v, 'system')}
                            onReset={() => onSave(entry.key, sKey, defaultVal, 'system')}
                            allowedValues={sMeta.validation?.allowed}
                          />
                        </td>
                      </>
                    )}

                    {/* Tenant override + effective */}
                    {activeLayer === 'tenant' && (
                      <>
                        <td className="px-3 py-3 align-top">
                          {canEditTenant ? (
                            <EditCell
                              value={tenantVal !== undefined ? tenantVal : (systemVal !== undefined ? systemVal : defaultVal)}
                              metaType={sMeta.type}
                              isDefault={tenantVal === undefined || tenantVal === null}
                              saving={savingKey === `tenant.${entry.key}.${sKey}`}
                              onSave={(v) => onSave(entry.key, sKey, v, 'tenant')}
                              onReset={() => onSave(entry.key, sKey, systemVal !== undefined ? systemVal : defaultVal, 'tenant')}
                              allowedValues={sMeta.validation?.allowed}
                            />
                          ) : tenantVal !== undefined && tenantVal !== null ? (
                            <OverridePill value={tenantVal} color="orange" />
                          ) : (
                            <span className="text-xs text-ui-text3 italic">— inherits</span>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <span className="text-xs font-mono font-semibold text-ui-text">{displayVal(effectiveVal !== undefined ? effectiveVal : defaultVal)}</span>
                        </td>
                      </>
                    )}

                    {/* Role override + effective */}
                    {activeLayer === 'role' && (
                      <>
                        <td className="px-3 py-3 align-top">
                          {canEditRole ? (
                            <EditCell
                              value={focusedRoleVal !== undefined ? focusedRoleVal : (tenantVal !== undefined ? tenantVal : (systemVal !== undefined ? systemVal : defaultVal))}
                              metaType={sMeta.type}
                              isDefault={focusedRoleVal === undefined || focusedRoleVal === null}
                              saving={savingKey === `role.${entry.key}.${sKey}`}
                              onSave={(v) => onSave(entry.key, sKey, v, 'role')}
                              onReset={() => onSave(entry.key, sKey, tenantVal !== undefined ? tenantVal : (systemVal !== undefined ? systemVal : defaultVal), 'role')}
                              allowedValues={sMeta.validation?.allowed}
                            />
                          ) : roleVals.length > 0 ? (
                            <div className="flex flex-col gap-1">{roleVals.map((r) => <OverridePill key={r.id} value={r.value} label={r.id} color="purple" />)}</div>
                          ) : (
                            <span className="text-xs text-ui-text3 italic">— inherits</span>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <span className="text-xs font-mono font-semibold text-ui-text">{displayVal(effectiveVal !== undefined ? effectiveVal : defaultVal)}</span>
                        </td>
                      </>
                    )}

                    {/* User override + effective */}
                    {activeLayer === 'user' && (
                      <>
                        <td className="px-3 py-3 align-top">
                          {canEditUser ? (
                            <EditCell
                              value={focusedUserVal !== undefined ? focusedUserVal : (tenantVal !== undefined ? tenantVal : (systemVal !== undefined ? systemVal : defaultVal))}
                              metaType={sMeta.type}
                              isDefault={focusedUserVal === undefined || focusedUserVal === null}
                              saving={savingKey === `user.${entry.key}.${sKey}`}
                              onSave={(v) => onSave(entry.key, sKey, v, 'user')}
                              onReset={() => onSave(entry.key, sKey, tenantVal !== undefined ? tenantVal : (systemVal !== undefined ? systemVal : defaultVal), 'user')}
                              allowedValues={sMeta.validation?.allowed}
                            />
                          ) : userVals.length > 0 ? (
                            <div className="flex flex-col gap-1">{userVals.map((u) => <OverridePill key={u.id} value={u.value} label={u.id} color="pink" />)}</div>
                          ) : (
                            <span className="text-xs text-ui-text3 italic">— inherits</span>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <span className="text-xs font-mono font-semibold text-ui-text">{displayVal(effectiveVal !== undefined ? effectiveVal : defaultVal)}</span>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {expanded && settings.length === 0 && (
        <div className="px-5 py-4 border-t border-ui-border2"><p className="text-xs text-ui-text3">No settings defined.</p></div>
      )}
    </div>
  );
};

// ── select field ─────────────────────────────────────────────────────────────

const FilterSelect: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  loading?: boolean;
}> = ({ label, value, onChange, options, placeholder = 'Select…', loading }) => (
  <div className="flex flex-col gap-1 w-full sm:w-auto sm:min-w-[180px]">
    <label className="text-xs font-medium text-ui-text2 uppercase tracking-wide">{label}</label>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className="w-full appearance-none border border-ui-border rounded-lg px-3 py-2 text-sm bg-ui-input focus:outline-none focus:ring-2 focus:ring-brand-dark/40 disabled:opacity-50 pr-8"
      >
        <option value="">{loading ? 'Loading…' : placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <svg className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-text3 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  </div>
);

// ── page ──────────────────────────────────────────────────────────────────────

const ConfigurationsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPrefix = searchParams.get('prefix') ?? '';
  const [search, setSearch] = useState(initialPrefix);
  const [activeLayer, setActiveLayer] = useState<Layer>('system');

  // Remove the ?prefix param from the URL after applying it so Back/refresh behave normally
  useEffect(() => {
    if (searchParams.has('prefix')) {
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  // catalogue query — passes scope params based on active layer
  const catalogueParams = useMemo(() => {
    if (activeLayer === 'system') return {};
    if (activeLayer === 'tenant') return selectedTenantId ? { scopeTenantId: selectedTenantId } : undefined;
    if (activeLayer === 'role') return selectedTenantId ? { scopeTenantId: selectedTenantId, ...(selectedRoleId ? { scopeRoleId: selectedRoleId } : {}) } : undefined;
    if (activeLayer === 'user') return selectedTenantId ? { scopeTenantId: selectedTenantId, ...(selectedUserId ? { scopeUserId: selectedUserId } : {}) } : undefined;
    return {};
  }, [activeLayer, selectedTenantId, selectedRoleId, selectedUserId]);

  const skipCatalogue = (activeLayer !== 'system') && !selectedTenantId;

  const { data, isLoading, refetch } = useListCatalogueEntriesQuery(
    catalogueParams ?? {},
    { skip: skipCatalogue, refetchOnMountOrArgChange: true }
  );
  const [updateConfig] = useUpdateCatalogueConfigMutation();
  const [deleteConfig] = useDeleteCatalogueConfigMutation();

  // tenant list for dropdown
  const { data: tenantsData, isLoading: tenantsLoading } = useListPlatformTenantsQuery(undefined, {
    skip: activeLayer === 'system',
  });

  // roles for selected tenant (role layer only)
  const { data: rolesData, isLoading: rolesLoading } = useListPlatformRolesQuery(
    { tenantId: selectedTenantId },
    { skip: activeLayer !== 'role' || !selectedTenantId }
  );

  // users for selected tenant (user layer only)
  const { data: usersData, isLoading: usersLoading } = useListPlatformUsersQuery(
    { tenant_id: selectedTenantId, limit: 200 },
    { skip: activeLayer !== 'user' || !selectedTenantId }
  );

  const entries: CatalogueEntry[] = data?.data ?? [];

  const tenantOptions = (tenantsData?.data ?? []).map((t) => ({
    value: t.tenant_id,
    label: t.company_name ?? t.tenant_id,
  }));

  const roleOptions = (rolesData?.data ?? []).map((r) => ({
    value: r.role_id,
    label: r.name ?? r.role_id,
  }));

  const userOptions = (usersData?.data ?? []).map((u) => ({
    value: u.user_id,
    label: u.name ? `${u.name} (${u.email ?? ''})` : (u.email ?? u.user_id),
  }));

  const filtered = useMemo(() => {
    if (!search.trim()) return entries;
    const q = search.toLowerCase();
    return entries.filter(
      (e) =>
        e.key.toLowerCase().includes(q) ||
        (e.description ?? '').toLowerCase().includes(q) ||
        Object.keys(e.settings ?? {}).some((s) => s.toLowerCase().includes(q))
    );
  }, [entries, search]);

  // Publish what this settings page is showing into the global UI-context store so SAM can answer
  // "what am I looking at?" — catalogue KEYS + active layer + scope only, never raw values
  // (permission scoping, SAM-FR-01-006).
  useRegisterUIContext(
    {
      mode: 'settings',
      surfaces: [{
        kind: 'settings',
        area: 'platform.configurations',
        activeLayer,
        visibleKeys: filtered.map((e) => e.key),
        scope: {
          tenantId: selectedTenantId || undefined,
          roleId: selectedRoleId || undefined,
          userId: selectedUserId || undefined,
        },
      }],
    },
    [activeLayer, filtered, selectedTenantId, selectedRoleId, selectedUserId],
  );

  const showToast = (type: 'error' | 'success', text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  const handleLayerChange = (layer: Layer) => {
    setActiveLayer(layer);
    setSelectedTenantId('');
    setSelectedRoleId('');
    setSelectedUserId('');
  };

  const handleTenantChange = (id: string) => {
    setSelectedTenantId(id);
    setSelectedRoleId('');
    setSelectedUserId('');
  };

  const handleSave = async (catalogueKey: string, settingKey: string, value: unknown, targetLayer: Layer) => {
    const key = `${targetLayer}.${catalogueKey}.${settingKey}`;
    setSavingKey(key);

    try {
      const scopeTenantId = targetLayer !== 'system' ? selectedTenantId : undefined;
      const scopeRoleId   = targetLayer === 'role' ? selectedRoleId : undefined;
      const scopeUserId   = targetLayer === 'user' ? selectedUserId : undefined;
      await updateConfig({ key: catalogueKey, value: { [settingKey]: value }, scopeTenantId, scopeRoleId, scopeUserId }).unwrap();
      showToast('success', `Saved ${settingKey}`);
      refetch();
    } catch (err: unknown) {
      showToast('error', getErrorDetail(err) ?? 'Failed to save.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleDelete = async (catalogueKey: string) => {
    if (!selectedTenantId) return;
    setDeletingKey(catalogueKey);
    try {
      const scopeRoleId = activeLayer === 'role' ? selectedRoleId || undefined : undefined;
      const scopeUserId = activeLayer === 'user' ? selectedUserId || undefined : undefined;
      await deleteConfig({ key: catalogueKey, scopeTenantId: selectedTenantId, scopeRoleId, scopeUserId }).unwrap();
      showToast('success', `Override removed — values now inherit from the layer below.`);
      refetch();
    } catch (err: unknown) {
      showToast('error', getErrorDetail(err) ?? 'Failed to delete override.');
    } finally {
      setDeletingKey(null);
    }
  };

  const LAYERS: { value: Layer; label: string; color: string }[] = [
    { value: 'system',  label: 'System',  color: 'blue'   },
    { value: 'tenant',  label: 'Tenant',  color: 'orange' },
    { value: 'role',    label: 'Role',    color: 'purple' },
    { value: 'user',    label: 'User',    color: 'pink'   },
  ];

  const DOT_CLASS: Record<string, string> = {
    blue:   'bg-blue-400',
    orange: 'bg-orange-400',
    purple: 'bg-purple-400',
    pink:   'bg-pink-400',
  };

  const needsTenantPick = activeLayer !== 'system' && !selectedTenantId;

  return (
    <PageShell
      maxWidth="max-w-6xl"
      header={
        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-bold text-ui-text">Platform Configurations</h1>
            <p className="text-sm text-ui-text2 mt-1">View and manage configuration overrides across all layers.</p>
          </div>

          {/* layer tabs */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-xs font-medium text-ui-text3 uppercase tracking-wide shrink-0">Layer</span>
            <div className="flex items-center gap-1 bg-ui-subtle rounded-lg p-1 overflow-x-auto">
              {LAYERS.map((l) => (
                <button
                  key={l.value}
                  onClick={() => handleLayerChange(l.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all shrink-0 whitespace-nowrap ${
                    activeLayer === l.value ? 'bg-ui-card text-brand-dark shadow-sm' : 'text-ui-text2 hover:text-ui-text'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${DOT_CLASS[l.color]}`} />
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {/* cascading sub-filters */}
          {activeLayer !== 'system' && (
            <div className="flex flex-wrap items-end gap-4">
              <FilterSelect
                label="Tenant"
                value={selectedTenantId}
                onChange={handleTenantChange}
                options={tenantOptions}
                placeholder="Select tenant…"
                loading={tenantsLoading}
              />
              {activeLayer === 'role' && selectedTenantId && (
                <FilterSelect
                  label="Role (optional)"
                  value={selectedRoleId}
                  onChange={setSelectedRoleId}
                  options={roleOptions}
                  placeholder="All roles"
                  loading={rolesLoading}
                />
              )}
              {activeLayer === 'user' && selectedTenantId && (
                <FilterSelect
                  label="User (optional)"
                  value={selectedUserId}
                  onChange={setSelectedUserId}
                  options={userOptions}
                  placeholder="All users"
                  loading={usersLoading}
                />
              )}
              {selectedTenantId && (
                <button
                  onClick={() => { setSelectedTenantId(''); setSelectedRoleId(''); setSelectedUserId(''); }}
                  className="text-xs text-ui-text3 hover:text-ui-text2 underline mb-2"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {/* search + result count */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-text3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7 7 0 104.65 4.65a7 7 0 0012 12z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search keys, settings, descriptions…"
                className="w-full pl-9 pr-8 py-2 border border-ui-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 bg-ui-input"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-ui-text3 hover:text-ui-text2">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>
            {!isLoading && !needsTenantPick && (
              <span className="text-xs text-ui-text3">{filtered.length} of {entries.length} catalogues</span>
            )}
          </div>
        </div>
      }
    >

      {/* toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {toast.type === 'success'
            ? <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            : <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
          {toast.text}
        </div>
      )}

      {/* content */}
      {needsTenantPick ? (
        <div className="border border-dashed border-ui-border rounded-xl py-20 text-center">
          <svg className="h-8 w-8 text-ui-text3 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <p className="text-sm text-ui-text2 font-medium">Select a tenant to view {activeLayer} overrides</p>
          <p className="text-xs text-ui-text3 mt-1">
            {activeLayer === 'role' ? 'Select a tenant, then optionally a role to enable inline editing.' :
             activeLayer === 'user' ? 'Select a tenant, then optionally a user to enable inline editing.' :
             'Use the Tenant dropdown above to scope the view.'}
          </p>
        </div>
      ) : isLoading ? (
        <div className="py-20 text-center">
          <div className="inline-block w-6 h-6 border-2 border-brand-dark border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-sm text-ui-text3">Loading configurations…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-ui-border rounded-xl py-20 text-center">
          <p className="text-sm text-ui-text3">No configurations match your filters.</p>
          {search && (
            <button onClick={() => setSearch('')} className="mt-2 text-xs text-brand-dark hover:underline">Clear search</button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((entry) => (
            <CatalogueCard
              key={entry.key}
              entry={entry}
              activeLayer={activeLayer}
              selectedTenantId={selectedTenantId}
              selectedRoleId={selectedRoleId}
              selectedUserId={selectedUserId}
              onSave={handleSave}
              savingKey={savingKey}
              onDelete={handleDelete}
              deletingKey={deletingKey}
              autoExpand={!!initialPrefix && entry.key === initialPrefix}
            />
          ))}
        </div>
      )}
    </PageShell>
  );
};

export default ConfigurationsPage;
